/**
 * Portadas del ultimo tomo publicado.
 *
 * La fuente da una portada por serie —casi siempre la del tomo 1— y en la
 * biblioteca se ve mejor el tomo por el que va la serie de verdad. Quien lo
 * averigua es el servicio del VPS (`servidor/tomos.py`), que empareja cada
 * serie con MangaDex y se baja la portada del tomo mas alto; aqui solo se
 * pregunta que hay y se pinta.
 *
 * **Solo manga**, que es lo que se pidio: el servicio descarta lo que no sea
 * japones de origen, asi que los manhwa se quedan con su portada de siempre
 * sin que el cliente tenga que saber nada.
 */

import { cabecerasAuth, ErrorApi } from './cliente'

/** Lo guardado en este aparato para pintar sin esperar al servidor. */
const CLAVE_CACHE = 'yomou.tomos'

export type EstadoTomo = 'lista' | 'sin' | 'apagado' | 'nada'

export interface Tomo {
  estado: EstadoTomo
  /** Titulo de la serie en MangaDex, que no siempre es el de la fuente. */
  titulo?: string
  /** Numero del ultimo tomo, tal cual lo escribe MangaDex ("115"). */
  volumen?: string
  uuid?: string
  /** Cuantos tomos tiene, para reconocer la serie buena entre las parecidas. */
  cuantos?: number
  /** true si la pareja la eligio el usuario: el automatico no la toca. */
  fijado?: boolean
}

export type ListaTomos = Record<number, Tomo>

export interface Candidato {
  uuid: string
  titulo: string
  idioma?: string
  ano?: number
  miniatura?: string | null
}

async function pedir<T>(ruta: string, cuerpo?: unknown): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      method: cuerpo === undefined ? 'GET' : 'POST',
      headers: { ...cabecerasAuth(), 'Content-Type': 'application/json' },
      body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
    })
  } catch {
    throw new ErrorApi('No se pudo conectar con el servidor')
  }
  if (!respuesta.ok) {
    const datos = (await respuesta.json().catch(() => null)) as { error?: string } | null
    throw new ErrorApi(datos?.error ?? `El servidor respondio ${respuesta.status}`, respuesta.status)
  }
  return (await respuesta.json()) as T
}

/**
 * Lo ultimo que se supo, guardado aqui.
 *
 * Sirve para dos cosas: que la biblioteca abra ya con las portadas buenas en
 * vez de cambiarlas a media carga, y que sin conexion se sigan viendo —la
 * imagen esta en IndexedDB, pero sin esta lista no se sabria ni que pedirla—.
 */
export function tomosGuardados(): ListaTomos {
  try {
    const crudo = localStorage.getItem(CLAVE_CACHE)
    return crudo ? (JSON.parse(crudo) as ListaTomos) : {}
  } catch {
    return {}
  }
}

export async function listaTomos(): Promise<ListaTomos> {
  const lista = await pedir<ListaTomos>('/tomos/lista')
  try {
    localStorage.setItem(CLAVE_CACHE, JSON.stringify(lista))
  } catch {
    // Sin sitio en el almacen se sigue funcionando, solo que sin adelanto
  }
  return lista
}

/**
 * La direccion de la portada de un tomo.
 *
 * Lleva el numero de tomo aunque el servidor no lo mire: asi, cuando sale un
 * tomo nuevo, **cambia la URL** y ni el cache del navegador ni el de la app
 * —que guarda las imagenes por su ruta— sirven la anterior.
 */
export function rutaPortadaTomo(mangaId: number, volumen: string): string {
  return `/tomos/portada/${mangaId}?tomo=${encodeURIComponent(volumen)}`
}

export function candidatosTomo(mangaId: number, titulo: string): Promise<Candidato[]> {
  return pedir<Candidato[]>(
    `/tomos/candidatos?manga=${mangaId}&titulo=${encodeURIComponent(titulo)}`,
  )
}

/** Fija con que serie de MangaDex va emparejada. Sin `uuid`, la apaga aqui. */
export function fijarTomo(mangaId: number, uuid: string | null, titulo: string): Promise<Tomo> {
  return pedir<Tomo>('/tomos/fijar', { manga: mangaId, uuid, titulo })
}

/** Deshace lo elegido a mano y deja que el automatico vuelva a mirarlo. */
export function soltarTomo(mangaId: number, titulo: string): Promise<Tomo> {
  return pedir<Tomo>('/tomos/soltar', { manga: mangaId, titulo })
}
